import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { MenuItem, MenuList, Popover } from '@mui/material';
import React, { useCallback, useState } from 'react';
import {
  // Button does not require permissions
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  Button as SimpleButton,
  useListContext,
  useTranslate,
} from 'react-admin';

import {
  InstrumentsPerformanceFilter,
  InstrumentsPerformanceFilterValues,
} from '@Common/types/bo/instruments';

type CurrentTarget = EventTarget & HTMLButtonElement;

export const PerformanceFilterButton = (props: { 'data-testid'?: string }) => {
  const [anchorEl, setAnchorEl] = useState<null | CurrentTarget>(null);
  const translate = useTranslate();
  const { filterValues, setFilters, page, setPage } = useListContext();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl],
  );

  const handleClose = useCallback((): void => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handlePerformanceSelect = useCallback(
    (performance: InstrumentsPerformanceFilter): void => {
      const currentPage = page;
      setFilters({ ...filterValues, performance }, null, false);
      // Immediately restore the page to prevent pagination reset
      // React will batch these updates in the same event handler
      setPage(currentPage);
      handleClose();
    },
    [filterValues, setFilters, page, setPage, handleClose],
  );

  const currentPerformance =
    (filterValues?.performance as InstrumentsPerformanceFilter | undefined) ?? 'position_3y';
  const shortLabel = translate(
    `resources.bo_common.instruments.performance_short.${currentPerformance}`,
  );
  const buttonLabel = `${translate<string>('resources.bo_common.instruments.fields.performance')}: ${shortLabel}`;

  return (
    <>
      <SimpleButton
        color="primary"
        onClick={handleClick}
        label={buttonLabel}
        disableElevation={true}
        data-testid={props['data-testid']}>
        <TrendingUpIcon />
      </SimpleButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}>
        <MenuList>
          {InstrumentsPerformanceFilterValues.map((filter) => (
            <MenuItem
              key={filter}
              selected={currentPerformance === filter}
              onClick={() => handlePerformanceSelect(filter)}
              data-testid={`${props['data-testid']}_MenuItem`}>
              {translate(`resources.bo_common.instruments.performance.${filter}`)}
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  );
};
